<template>
  <div
    class="relative overflow-hidden w-full"
    :class="[backgroundColor,
             (isMini && isBoxFull) ? 'lg:flex-1 lg:flex lg:flex-row-reverse lg:justify-end' : '',
             isHalf ? 'flex flex-row-reverse' : '']"
  >
    <div
      :class="[isHalf ? 'w-1/2' : '', bannerImageHeight,
               (isMini && isBoxFull) ? 'lg:flex-1' : '',]"
    >
      <ImgView
        :src="image"
        class="object-cover"
        :class="mobileImage ? 'hidden lg:block' : ''"
      />
      <ImgView
        :src="mobileImage"
        class="w-full object-cover"
        :class="mobileImage ? 'block lg:hidden' : 'hidden'"
      />
    </div>
    <div
      class="absolute items-center justify-center transform rotate-45
        top-4 -right-[7.5rem] h-12 w-80"
      :class="[displayStrap ? 'flex' : 'hidden',
               strapBackground]"
    >
      <i
        class="text-4xl transform -rotate-45"
        :class="[strapIconClass, strapIconColor]"
      />
    </div>
    <div
      class="flex"
      :class="[
        isBoxFull ? 'bottom-0 left-0 h-full' : '',
        isHalf || isBoxFull ? 'w-1/2' : 'w-full lg:absolute lg:w-[300px]',
        (isHalf && !isMini) ? 'lg:absolute' : '',
        isMini ? 'h-[88px]': '',
        boxBackground,
        isBoxFull ? 'w-full lg:w-[300px]' : '',
        (isMini && isBoxFull) ? 'block lg:pl-5' : '',
        (!isMini && !isBoxFull && !isMedium) ? 'bottom-4 left-4 min-h-[188px] lg:min-h-64' : '',
        isMedium ? 'bottom-0 left-0 h-auto lg:h-full' : '']"
    >
      <div
        class="flex flex-col gap-2 w-full"
        :class="[textColor,
                 isMini ? 'm-2 lg:m-5' : '',
                 isFullImage ? 'm-10 justify-around': '',
                 (isMini && isBoxFull) ? 'lg:justify-center' : '',
                 (!isMini && !isFullImage ? 'm-5 justify-between' : '')]"
      >
        <ProductCardBadge
          :badge-translation-key="badgeText"
          :badge-class="badgeColor"
          :class="badgeText ? 'block w-max' : 'hidden'"
        />
        <div
          v-if="headerImage"
          class="flex gap-x-2 items-center"
        >
          <component
            :is="headerType"
            class="font-bold"
            :class="[isFullImage ? 'text-3xl' : 'text-2xl']"
          >
            <ImgView
              :src="headerImage"
              :alt="headerImageAlt"
            />
          </component>
        </div>
        <div
          v-else
          class="flex gap-x-2 items-center"
        >
          <div v-if="headerIconClass">
            <span :class="headerClasses" />
          </div>
          <component
            :is="headerType"
            class="font-bold"
            :class="[isFullImage ? 'text-3xl' : 'text-2xl']"
          >
            {{ header }}
          </component>
        </div>
        <DisplayHtmlDangerously
          class="flex-grow"
          :html-text="description"
        />
        <CustomButton
          v-if="hasButton && buttonText"
          :type="buttonType"
          :class="[isFullImage || isMedium ? '' : 'mt-auto']"
          :text="buttonText"
          :url="link"
          :is-round-chevron-mobile="isButtonRoundMobile"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import CustomButton from '~/components/atoms/Buttons/Button/CustomButton.vue';
import ImgView from '~/components/atoms/ImgView/ImgView.vue';
import ProductCardBadge from '~/components/atoms/Product/ProductCardBadge.vue';
import DisplayHtmlDangerously from '~/components/atoms/DisplayHtmlDangerously/DisplayHtmlDangerously.vue';

export default defineComponent({
  name: 'BannerBox',
  components: {
    CustomButton,
    ImgView,
    ProductCardBadge,
    DisplayHtmlDangerously,
  },
  props: {
    strapIconClass: {
      type: String,
      default: '',
    },
    strapIconColor: {
      type: String,
      default: 'text-EXPONDO-white',
    },
    displayStrap: {
      type: Boolean,
      default: false,
    },
    image: {
      type: String,
      required: true,
    },
    mobileImage: {
      type: String,
      default: '',
    },
    mobileImageAutoHeight: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: '',
    },
    headerType: {
      type: String,
      default: 'p',
    },
    headerIconClass: {
      type: String,
      default: '',
    },
    headerIconColor: {
      type: String,
      default: '',
    },
    headerImage: {
      type: String,
      default: '',
    },
    headerImageAlt: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      required: true,
    },
    buttonText: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      required: true,
    },
    isFullImage: {
      type: Boolean,
      default: false,
    },
    isBoxFull: {
      type: Boolean,
      default: false,
    },
    hasButton: {
      type: Boolean,
      default: true,
    },
    isMini: {
      type: Boolean,
      default: false,
    },
    isMedium: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    boxBackground: {
      type: String,
      default: '',
    },
    strapBackground: {
      type: String,
      default: '',
    },
    isHalf: {
      type: Boolean,
      default: false,
    },
    textColor: {
      type: String,
      default: 'text-EXPONDO-white',
    },
    badgeText: {
      type: String,
      default: '',
    },
    isButtonRoundMobile: {
      type: Boolean,
      default: false,
    },
    buttonType: {
      type: String,
      default: 'primary',
    },
    badgeColor: {
      type: String,
      default: 'bg-EXPONDO-white',
    },
  },
  setup(props) {

    const autoHeightMobileBanner = computed(() => {

      return props.mobileImage && props.mobileImageAutoHeight;
    
    });

    const bannerImageHeight = computed(() => {

      let heightClass = '';

      if (autoHeightMobileBanner.value) {

        heightClass = 'h-auto';

        if (props.isMini) {

          heightClass += ' lg:h-auto';
        
        } else if (props.isMedium) {

          heightClass += ' lg:h-[260px]';
        
        } else {

          heightClass += ' lg:h-[360px]';
        
        }

        return heightClass;
      
      }

      if (props.isMini) {

        heightClass = 'h-auto';
      
      } else if (props.isMedium) {

        heightClass = 'lg:h-[260px]';
      
      } else {

        heightClass = 'h-[200px] lg:h-[360px]';
      
      }

      return heightClass;
    
    });

    const headerClasses = computed(() => {

      return [
        props.isFullImage ? 'text-3xl' : 'text-2xl',
        props.headerIconClass,
        props.headerIconColor,
      ];
    
    });

    return {
      bannerImageHeight,
      headerClasses,
    };
  
  },
});
</script>
